/**
 * Auto-Resubscribe Hook for RSVP Actions
 * Automatically resubscribes users to reminder emails when they RSVP to events
 */

/**
 * Auto-resubscribe user when they RSVP to an event
 * This should be called after successful RSVP submission
 * 
 * @param {string} userEmail - Email of user who RSVPed
 * @param {string} graduateEmail - Email of graduate/event owner
 * @param {string} rsvpStatus - RSVP status ('Going', 'Maybe', 'Decline')
 */
export const handleAutoResubscribe = async (userEmail, graduateEmail, rsvpStatus) => {
    // Only resubscribe for "Going" and "Maybe" RSVPs, not "Decline"
    if (rsvpStatus !== 'Going' && rsvpStatus !== 'Maybe') {
        console.log(`⚠️ Not auto-resubscribing for RSVP status: ${rsvpStatus}`);
        return {
            success: false,
            reason: 'Only Going/Maybe RSVPs trigger resubscription'
        };
    }

    if (!userEmail || !graduateEmail) {
        console.error('❌ Missing email addresses for auto-resubscribe:', { userEmail, graduateEmail });
        return {
            success: false,
            reason: 'Missing required email addresses'
        };
    }

    try {
        // Client-side API request
        const SERVER_URL = process.env.NODE_ENV === 'production' 
            ? 'https://us-central1-my-tassel.cloudfunctions.net/api'
            : 'https://us-central1-my-tassel.cloudfunctions.net/api';

        const response = await fetch(`${SERVER_URL}/resubscribe-to-reminders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: userEmail.toLowerCase(),
                graduateEmail: graduateEmail.toLowerCase(),
                reason: `User voluntarily RSVPed ${rsvpStatus} to event`
            }),
        });

        const result = await response.json();

        if (result.success) {
            console.log(`✅ Auto-resubscribed ${userEmail} to ${graduateEmail}'s reminder emails via ${rsvpStatus} RSVP`);
        } else {
            console.log(`ℹ️ Auto-resubscribe for ${userEmail}: ${result.message}`);
        }

        return result;
    } catch (error) {
        console.error(`❌ Error auto-resubscribing ${userEmail}:`, error);
        return {
            success: false,
            error: error.message,
            reason: 'Failed to process auto-resubscription'
        };
    }
};

/**
 * Auto-subscribe user when they're invited to view a page
 * This should be called when sending invitations
 * 
 * @param {string} inviteeEmail - Email of person being invited
 * @param {string} graduateEmail - Email of graduate sending invitation  
 * @param {string} inviteMethod - How they were invited ('email' or 'sms')
 */
export const handleAutoSubscribe = async (inviteeEmail, graduateEmail, inviteMethod = 'email') => {
    if (!inviteeEmail || !graduateEmail) {
        console.error('❌ Missing email addresses for auto-subscribe:', { inviteeEmail, graduateEmail });
        return {
            success: false,
            reason: 'Missing required email addresses'
        };
    }

    try {
        // Client-side API request
        const SERVER_URL = process.env.NODE_ENV === 'production' 
            ? 'https://us-central1-my-tassel.cloudfunctions.net/api'
            : 'https://us-central1-my-tassel.cloudfunctions.net/api';

        const response = await fetch(`${SERVER_URL}/subscribe-to-reminders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: inviteeEmail.toLowerCase(),
                graduateEmail: graduateEmail.toLowerCase(),
                inviteMethod: inviteMethod
            }),
        });

        const result = await response.json();

        if (result.success) {
            console.log(`✅ Auto-subscribed ${inviteeEmail} to ${graduateEmail}'s reminder emails via ${inviteMethod} invitation`);
        } else {
            console.log(`⚠️ Auto-subscribe for ${inviteeEmail}: ${result.reason}`);
        }

        return result;
    } catch (error) {
        console.error(`❌ Error auto-subscribing ${inviteeEmail}:`, error);
        return {
            success: false,
            error: error.message,
            reason: 'Failed to process auto-subscription'
        };
    }
};